import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const RoleBasedRoute = ({ 
  children, 
  requiredRoles = [], 
  requiredPermissions = [], 
  fallbackComponent = null,
  redirectTo = '/unauthorized'
}) => {
  const {
    currentUser,
    userData,
    loading,
    initialized,
    hasAnyRole,
    hasPermission,
    refreshUserProfile,
  } = useAuth();
  const location = useLocation();
  const [profileStatus, setProfileStatus] = useState('idle');

  console.log('[RoleBasedRoute] invoked', {
    requiredRoles,
    requiredPermissions,
    userId: currentUser?.uid || null,
    loading,
    hasProfile: Boolean(userData),
    initialized,
    profileStatus,
  });

  useEffect(() => {
    if (
      initialized &&
      !loading &&
      currentUser &&
      !userData &&
      (profileStatus === 'idle' || profileStatus === 'retrying')
    ) {
      setProfileStatus('loading');
      refreshUserProfile()
        .then((result) => {
          if (result?.success && result?.user) {
            setProfileStatus('loaded');
          } else {
            setProfileStatus('error');
          }
        })
        .catch(() => {
          setProfileStatus('error');
        });
    }
  }, [initialized, loading, currentUser, userData, profileStatus, refreshUserProfile]);

  const handleRetryProfile = () => {
    if (!userData) {
      setProfileStatus('retrying');
      return;
    }

    setProfileStatus('loading');
    refreshUserProfile()
      .then((result) => {
        if (result?.success && result?.user) {
          setProfileStatus('loaded');
        } else {
          setProfileStatus('error');
        }
      })
      .catch(() => {
        setProfileStatus('error');
      });
  };

  // Show loading spinner while checking auth state
  if (!initialized || loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-brand-mid"></div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!currentUser) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Wait for user profile to load
  if (!userData) {
    if (profileStatus === 'loading' || profileStatus === 'retrying') {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-brand-mid"></div>
        </div>
      );
    }

    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center max-w-md">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">We couldn't load your profile</h2>
          <p className="text-sm text-gray-600">
            Please refresh your access. If the issue persists, try signing out and back in or contact your administrator.
          </p>
          <button
            type="button"
            onClick={handleRetryProfile}
            className="mt-4 btn-primary text-sm"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Check if user account is active
  if (!userData.isActive) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Account Inactive</h1>
          <p className="text-gray-600">Your account has been deactivated. Please contact your administrator.</p>
        </div>
      </div>
    );
  }

  // Check role requirements
  const normalizedRoles = Array.isArray(requiredRoles) ? requiredRoles : [requiredRoles];
  const hasRequiredRole = normalizedRoles.length === 0 || hasAnyRole(normalizedRoles);
  
  // Check permission requirements
  const normalizedPermissions = Array.isArray(requiredPermissions)
    ? requiredPermissions
    : [requiredPermissions];
  const hasRequiredPermissions = normalizedPermissions.length === 0 || 
    normalizedPermissions.every(permission => hasPermission(permission));

  // If user doesn't have required role or permissions
  if (!hasRequiredRole || !hasRequiredPermissions) {
    // Show fallback component if provided
    if (fallbackComponent) {
      return fallbackComponent;
    }
    
    // Show unauthorized message
    if (redirectTo === '/unauthorized') {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h1>
            <p className="text-gray-600 mb-4">
              You don't have permission to access this page.
            </p>
            <div className="text-sm text-gray-500">
              {requiredRoles.length > 0 && (
                <p>Required roles: {requiredRoles.join(', ')}</p>
              )}
              {requiredPermissions.length > 0 && (
                <p>Required permissions: {requiredPermissions.join(', ')}</p>
              )}
              <p>Your role: {userData.role}</p>
            </div>
            <button
              type="button"
              onClick={handleRetryProfile}
              className="mt-4 inline-flex items-center px-4 py-2 rounded-md bg-blue-600 text-white text-sm font-medium shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Refresh Access
            </button>
          </div>
        </div>
      );
    }
    
    // Redirect to specified route
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // Render protected content if user has required role/permissions
  return children;
};

// Convenience components for specific roles
export const AdminRoute = ({ children, ...props }) => (
  <RoleBasedRoute requiredRoles={['admin']} {...props}>
    {children}
  </RoleBasedRoute>
);

export const ManagerRoute = ({ children, ...props }) => (
  <RoleBasedRoute requiredRoles={['admin', 'manager']} {...props}>
    {children}
  </RoleBasedRoute>
);

export const DispatcherRoute = ({ children, ...props }) => (
  <RoleBasedRoute requiredRoles={['admin', 'manager', 'dispatcher']} {...props}>
    {children}
  </RoleBasedRoute>
);

// Convenience component for permission-based routes
export const PermissionBasedRoute = ({ children, permissions, ...props }) => (
  <RoleBasedRoute requiredPermissions={permissions} {...props}>
    {children}
  </RoleBasedRoute>
);

export default RoleBasedRoute;
